import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/SupabaseAuthContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegistrationPage from "./pages/RegistrationPage";
import FamiliaDashboard from "./pages/FamiliaDashboard";
import AssistenteFamiliareApp from "./pages/AssistenteFamiliareApp";
import AccountDeletedPage from "./pages/AccountDeletedPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AdminDashboard from "./pages/AdminDashboard";
import TutorialPage from "./pages/TutorialPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import TermsPage from "./pages/TermsPage";
import CancelSubscriptionPage from "./pages/CancelSubscriptionPage";
import StazioneSuccessPage from "./pages/StazioneSuccessPage";
import JoinFamilyPage from "./pages/JoinFamilyPage";
import BlogPage from "./pages/BlogPage";
import ScudoPage from "./pages/ScudoPage";
import InvitoPage from "./pages/InvitoPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Components
import WhatsAppButton from "./components/WhatsAppButton";
import { MetaPixelTracker } from "./components/MetaPixelTracker";

// One-time cleanup: remove old custom service-worker.js and its caches
const cleanupOldServiceWorker = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        const swUrl = reg.active?.scriptURL || reg.waiting?.scriptURL || reg.installing?.scriptURL || '';
        if (swUrl.endsWith('/service-worker.js')) {
          await reg.unregister();
          console.log('[CuraCheck] Unregistered old service-worker.js');
        }
      }
    }
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const name of cacheNames) {
        if (name.startsWith('curacheck-')) {
          await caches.delete(name);
          console.log('[CuraCheck] Deleted cache:', name);
        }
      }
    }
  } catch (err) {
    // Never block rendering
    console.log('[CuraCheck] Cleanup error (non-blocking):', err);
  }
};

/**
 * AssistantAutoRedirect - Gestisce l'auto-login per l'assistente
 * Se l'utente ha un token salvato in localStorage, lo reindirizza automaticamente
 * all'app assistente senza richiedere login
 */
const AssistantAutoRedirect = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Controlla se c'è un token salvato in localStorage
    const savedToken = localStorage.getItem('curacheck_assistant_token');
    
    if (savedToken) {
      // Reindirizza all'app con il token salvato
      navigate(`/app/${savedToken}`, { replace: true });
    } else {
      // Nessun token salvato, vai alla landing page
      navigate('/', { replace: true });
    }
  }, [navigate]);
  
  // Mostra un loading mentre controlla
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAF9] to-[#E8F5E9] flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-[#4A7C59] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#4A7C59] font-medium">Accesso in corso...</p>
      </div>
    </div>
  );
};

function App() {
  useEffect(() => {
    cleanupOldServiceWorker();
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <MetaPixelTracker />
          <Routes>
            {/* PUBLIC: Main Landing Page */}
            <Route path="/" element={<LandingPage />} />
            
            {/* PUBLIC: All pages now accessible */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/registrazione" element={<RegistrationPage />} />
            <Route path="/dashboard" element={<FamiliaDashboard />} />
            <Route path="/pagamento-successo" element={<PaymentSuccessPage />} />
            
            {/* APP ASSISTENTE: Con token nell'URL */}
            <Route path="/app/:token" element={<AssistenteFamiliareApp />} />
            
            {/* APP ASSISTENTE: Senza token - auto-redirect se ha token salvato */}
            <Route path="/app" element={<AssistantAutoRedirect />} />
            
            {/* Legacy /preview routes - redirect to main routes */}
            <Route path="/preview" element={<Navigate to="/" replace />} />
            <Route path="/preview/login" element={<Navigate to="/login" replace />} />
            <Route path="/preview/registrazione" element={<Navigate to="/registrazione" replace />} />
            <Route path="/preview/dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/preview/pagamento-successo" element={<Navigate to="/pagamento-successo" replace />} />
            <Route path="/preview/app/:token" element={<Navigate to="/app/:token" replace />} />
            
            {/* Admin Dashboard - Protected with its own auth */}
            <Route path="/admin-private-control" element={<AdminDashboard />} />
            <Route path="/admin" element={<AdminDashboard />} />
            
            {/* Tutorial Page - Public */}
            <Route path="/tutorial" element={<TutorialPage />} />
            <Route path="/guida" element={<TutorialPage />} />
            
            {/* Legal Pages - Public (Iubenda Embeds) */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/termini-e-condizioni" element={<TermsPage />} />
            <Route path="/cancella-iscrizione" element={<CancelSubscriptionPage />} />
            <Route path="/account-eliminato" element={<AccountDeletedPage />} />
            
            {/* Join Family via QR/Link */}
            <Route path="/join-family/:famigliaId" element={<JoinFamilyPage />} />
            
            {/* Invito Destinatario - Family member invite */}
            <Route path="/invito/:inviteToken" element={<InvitoPage />} />
            
            {/* Blog - Soro Integration */}
            <Route path="/blog" element={<BlogPage />} />
            
            {/* Stazione Digitale - QR Pairing */}
            <Route path="/stazione-successo" element={<StazioneSuccessPage />} />
            
            {/* Scudo Emergency - Public page for assisted person */}
            <Route path="/scudo/:famigliaId" element={<ScudoPage />} />
            {/* /stazione-digitale è il link sul QR code - redirect alla pagina di pairing */}
            <Route path="/stazione-digitale" element={<Navigate to="/" replace />} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          <Toaster 
            position="top-center"
            expand={false}
            richColors
            toastOptions={{
              style: {
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '16px',
              },
            }}
          />
          
          {/* WhatsApp Floating Button - Assistenza */}
          <WhatsAppButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
